import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { HubConnectionState } from '@microsoft/signalr';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Subject, Subscription, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ETypeLien } from '../entites/enums/type-lien-document';
import { ProfilService } from '../services/profil-service';
import { SihcHubService, ReponseLock, ModifyingEmployee } from '../services/sihc-hub.service';

export interface LockState {
  isLocked: boolean;
  modifyingUsers: ModifyingEmployee[];
  currentUser: string;
  canModify: boolean;
}

@Component({
  selector: 'app-document-lock',
  templateUrl: './document-lock.component.html',
  styleUrls: ['./document-lock.component.scss'],
  providers: [MessageService, ConfirmationService]
})
export class DocumentLockComponent implements OnInit, OnDestroy, OnChanges {
  @Input() id: string;
  @Input() eTypeLien: ETypeLien;
  @Input() isModifying: boolean = false;

  @Output() modifyStatusChange = new EventEmitter<boolean>();
  @Output() saveRequested = new EventEmitter<void>();
  @Output() cancelRequested = new EventEmitter<Event>();

  private lockTimer: Subscription;
  private destroy$ = new Subject<void>();

  public lockState: LockState = {
    isLocked: false,
    modifyingUsers: [],
    currentUser: '',
    canModify: true
  };

  private lockTimeout: any;
  private currentEmployeeNumber: string = '';

  constructor(
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private profilService: ProfilService,
    private documentLockHub: SihcHubService
  ) {
    if (this.profilService.ProfilCourant()) {
      this.currentEmployeeNumber = this.profilService.ProfilCourant().noEmploye;
    }
  }

  ngOnInit(): void {
    this.setupHubConnection();

    if (this.profilService.ProfilCourant()) {
      this.lockState.currentUser = this.profilService.ProfilCourant().noEmploye;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isModifying && changes.isModifying.currentValue !== changes.isModifying.previousValue) {
      if (changes.isModifying.currentValue) {
        this.startLockUpdates();
      } else {
        this.stopLockUpdates();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.unsubscribe();
    this.stopLockUpdates();
  }

  private setupHubConnection(): void {
    this.documentLockHub.lock$
      .pipe(takeUntil(this.destroy$))
      .subscribe((response: ReponseLock) => {
        let lockIdLocal = `lock:${ETypeLien[this.eTypeLien]}-${this.id}`;
        if (response.id === lockIdLocal) {
          this.resetLockTimeout();

          if (response.employe) {
            // If it's not the current user who's modifying
            if (response.employe.noEmploye !== this.currentEmployeeNumber) {
              this.lockState.isLocked = true;

              // Add to modifying users if not already there
              if (!this.lockState.modifyingUsers.some(user => user.noEmploye === response.employe.noEmploye)) {
                this.lockState.modifyingUsers.push(response.employe);
              }
            }
          }
        }
      });

    this.documentLockHub.connect().then(() => {
      this.subscribe();
    });
  }

  private subscribe(): void {
    this.documentLockHub.subscribeLock(this.eTypeLien, this.id);
  }

  private unsubscribe(): void {
    this.documentLockHub.unsubscribeLock(this.eTypeLien, this.id);
  }

  private startLockUpdates(): void {
    // Send an initial lock
    this.sendLock();

    // Send a lock every 20 seconds
    this.lockTimer = interval(20000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.sendLock();
      });
  }

  private stopLockUpdates(): void {
    if (this.lockTimer) {
      this.lockTimer.unsubscribe();
    }
  }

  private sendLock(): void {
    this.documentLockHub.sendLock(this.eTypeLien, this.id);
  }

  private resetLockTimeout(): void {
    // Clear existing timeout
    if (this.lockTimeout) {
      clearTimeout(this.lockTimeout);
    }

    // Set new timeout (30 seconds)
    this.lockTimeout = setTimeout(() => {
      this.lockState.isLocked = false;
      this.lockState.modifyingUsers = [];
    }, 30000);
  }

  public modify(): void {
    if (this.lockState.isLocked) {
      this.confirmationService.confirm({
        message: 'Un autre utilisateur est en train de modifier ce document. Si vous continuez, vous pourriez perdre vos modifications ou l\'autre utilisateur pourrait perdre ses modifications.',
        header: 'Confirmation',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.startModifying();
        }
      });
    } else {
      this.startModifying();
    }
  }

  private startModifying(): void {
    this.modifyStatusChange.emit(true);
  }

  public save(): void {
    this.saveRequested.emit();
  }

  public cancel(event: Event): void {
    this.cancelRequested.emit(event);
  }

  // Helper method to display user's full name
  public getFullName(employe: ModifyingEmployee): string {
    return `${employe.prenom} ${employe.nom}`;
  }

  public getInitials(employe: ModifyingEmployee): string {
    return `${employe.prenom.charAt(0)}${employe.nom.charAt(0)}`;
  }

  // Helper method to create tooltip text for modifying users
  public getModifyingUsersTooltip(): string {
    if (this.lockState.modifyingUsers.length === 0) {
      return '';
    }

    const userNames = this.lockState.modifyingUsers.map(user => this.getFullName(user)).join(', ');
    return `Modification en cours par: ${userNames}`;
  }
} 